import {
  CdkVirtualScrollViewport,
  ScrollingModule,
} from '@angular/cdk/scrolling';
import {
  DecimalPipe,
  NgComponentOutlet,
  NgTemplateOutlet,
} from '@angular/common';
import type { AfterViewInit, OnInit, TemplateRef } from '@angular/core';
import {
  Component,
  DestroyRef,
  HostListener,
  Injector,
  computed,
  effect,
  inject,
  input,
  linkedSignal,
  model,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import {
  difference,
  differenceBy,
  get,
  intersectionBy,
  isArray,
  isFunction,
  isObject,
  orderBy,
} from 'lodash';
import {
  fromEventPattern,
  map,
  of,
  skip,
  startWith,
  switchMap,
  tap,
} from 'rxjs';
import type {
  OnyxOption,
  OnyxOptionsGroup,
} from '../../../directives/interfaces';
import { OnyxDropdownDirective } from '../../../directives/onyx-dropdown.directive';
import { OnyxTooltipDirective } from '../../../directives/onyx-tooltip.directive';
import { OnyxFormMode } from '../../../enums';
import { UtilityHelper } from '../../../helpers';
import { OverflowHelper } from '../../../helpers/overflow.helper';
import { I18N_NAMESPACE } from '../../../internal/constants';
import { OnyxModalService } from '../../../services/onyx-modal.service';
import { OnyxUserSettingsService } from '../../../services/onyx-user-settings.service';
import { OnyxIconButtonComponent } from '../../buttons/onyx-icon-button/onyx-icon-button.component';
import { OnyxCheckboxComponent } from '../../checkbox/onyx-checkbox/onyx-checkbox.component';
import { OnyxDropdownComponent } from '../../dropdown/onyx-dropdown/onyx-dropdown.component';
import { OnyxEmojiComponent } from '../../emoji/onyx-emoji/onyx-emoji.component';
import { OnyxEmptyStateComponent } from '../../empty-state/onyx-empty-state/onyx-empty-state.component';
import { OnyxLoadingErrorComponent } from '../../error/onyx-loading-error/onyx-loading-error.component';
import type { OnyxIcon, OnyxIconName } from '../../icons/interfaces';
import { OnyxIconComponent } from '../../icons/onyx-icon/onyx-icon.component';
import { OnyxLoadingBarComponent } from '../../loading-bar/onyx-loading-bar/onyx-loading-bar.component';
import type { OnyxPagination } from '../../pagination/interfaces';
import { OnyxPaginationComponent } from '../../pagination/onyx-pagination/onyx-pagination.component';
import { OnyxRadioButtonComponent } from '../../radio-button/onyx-radio-button/onyx-radio-button.component';
import { OnyxRibbonComponent } from '../../ribbon/onyx-ribbon/onyx-ribbon.component';
import { OnyxSpinnerComponent } from '../../spinner/onyx-spinner/onyx-spinner.component';
import {
  ONYX_TABLE_BATCH_ACTIONS_HIDE_DURATION,
  ONYX_TABLE_VIEW_DEFAULT_ICON,
} from '../constants';
import {
  OnyxTableAction,
  OnyxTableColor,
  OnyxTableRowSize,
  OnyxTableSelectionType,
} from '../enums';
import type {
  OnyxPaginated,
  OnyxPaths,
  OnyxTableColumn,
  OnyxTableCustomAction,
  OnyxTableData,
  OnyxTableNotFound,
  OnyxTableRowOptions,
  OnyxTableView,
  OnyxTableViewIcon,
} from '../interfaces';
import type {
  OnyxTableViewModalData,
  OnyxTableViewModalResult,
} from './onyx-table-view-modal/onyx-table-view-modal.component';
import { OnyxTableViewModalComponent } from './onyx-table-view-modal/onyx-table-view-modal.component';

@Component({
  selector: 'onyx-table',
  imports: [
    ScrollingModule,
    OnyxSpinnerComponent,
    OnyxPaginationComponent,
    TranslatePipe,
    OnyxEmptyStateComponent,
    OnyxIconComponent,
    NgTemplateOutlet,
    NgComponentOutlet,
    OnyxTooltipDirective,
    OnyxCheckboxComponent,
    OnyxRadioButtonComponent,
    ReactiveFormsModule,
    OnyxIconButtonComponent,
    OnyxIconComponent,
    OnyxRibbonComponent,
    OnyxDropdownDirective,
    OnyxDropdownComponent,
    DecimalPipe,
    NgTemplateOutlet,
    OnyxLoadingErrorComponent,
    OnyxLoadingBarComponent,
    OnyxEmojiComponent,
  ],
  templateUrl: './onyx-table.component.html',
  styleUrl: './onyx-table.component.scss',
})
export class OnyxTableComponent<T> implements OnInit, AfterViewInit {
  protected readonly overflowHelper = inject(OverflowHelper);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly modalService = inject(OnyxModalService);
  private readonly userSettingsService = inject(OnyxUserSettingsService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);

  public readonly data = input.required({
    transform: (
      data: OnyxPaginated<T> | T[] | null,
    ): OnyxPaginated<T> | null => {
      if (data == null) return null;

      const items = isArray(data) ? data : data.items;
      this.selectedItems.update((selectedItems) => {
        const existingSelectedItems = differenceBy(
          selectedItems,
          items,
          (item) => this.getItemId(item),
        );
        const updatedSelectedItems = intersectionBy(
          items,
          selectedItems,
          (item) => this.getItemId(item),
        );
        return [...existingSelectedItems, ...updatedSelectedItems];
      });

      if (isArray(data)) {
        return {
          items,
          totalItems: items.length,
          page: 1,
          limit: items.length,
        };
      }
      return data;
    },
  });
  public readonly columns = input.required<OnyxTableColumn<T>[]>();
  public readonly loading = input(false);
  public readonly showVerticalBorders = input(true);
  public readonly showHeader = input(true);
  public readonly defaultViews = input<OnyxTableView[]>();
  public readonly customViews = input<string>();
  public readonly view = model<OnyxTableView | null>(null);

  public readonly selectionType = input<OnyxTableSelectionType>();
  public readonly selectionDisabled =
    input<(data: OnyxTableData<T>) => boolean>();
  public readonly compareKey = input<OnyxPaths<T>>();
  public readonly selectedItems = model<T[]>([]);
  public readonly selectedItemsIdChange = output<unknown[]>();
  public readonly batchActionsTemplateRef = input<TemplateRef<any>>();

  public readonly tableShadow = input(true);
  public readonly rowSize = input(OnyxTableRowSize.SMALL);
  public readonly rowColor =
    input<(data: OnyxTableData<T>) => OnyxTableColor>();
  public readonly rowOptions = input<OnyxTableRowOptions<T>>();
  public readonly rowOptionTemplateRef = input<TemplateRef<any>>();
  public readonly rowClick = output<OnyxTableData<T>>();

  public readonly notFound = input<OnyxTableNotFound>({});
  public readonly filtered = input(false);
  public readonly clearFilters = input<() => void>();
  public readonly error = model(false);
  public readonly pagination = model<OnyxPagination>();
  public readonly paginationShowLimit = input(true);
  public readonly paginationPadding = input(8);

  protected readonly I18N = `${I18N_NAMESPACE}.table`;
  protected readonly SELECTION_COLUMN_WIDTH = 32;
  protected readonly OPTIONS_COLUMN_WIDTH = 48;
  private readonly LOADING_BODY_HEIGHT = 160;
  private readonly EMPTY_BODY_HEIGHT = 296;
  private readonly DEFAULT_NOT_FOUND_ICON: OnyxIcon = {
    name: 'no-results-outlined',
    size: 32,
  };
  protected readonly DEFAULT_NOT_FOUND_TITLE = `${this.I18N}.noData`;
  protected readonly BATCH_ACTIONS_HIDE_DURATION =
    ONYX_TABLE_BATCH_ACTIONS_HIDE_DURATION;

  protected readonly OnyxTableSelectionType = OnyxTableSelectionType;
  protected readonly OnyxTableColor = OnyxTableColor;
  protected readonly get = get;
  protected readonly isFunction = isFunction;

  protected initialLoading = computed(
    () => this.data() == null && !this.error(),
  );
  protected bodyHeight = linkedSignal({
    source: () => ({
      rows: this.data()?.items?.length,
      rowSize: this.rowSize(),
      loading: this.initialLoading(),
    }),
    computation: ({ rows, rowSize, loading }, previous?: { value: number }) => {
      if (loading && previous) return previous.value;
      if (rows === 0) return this.EMPTY_BODY_HEIGHT;

      return rows ? rows * rowSize : this.LOADING_BODY_HEIGHT;
    },
  });
  protected visibleColumns = computed(() => {
    const view = this.view() ?? this.defaultViews()?.[0];
    const visibleColumns = this.columns().filter((column) => {
      if (view == null) return true;
      return view.value.columns.includes(column.id);
    });
    const mappedColumns = visibleColumns.map((column) => ({
      ...column,
      actions: this.mapActions(column.actions),
    }));
    const orderedColumns = orderBy(mappedColumns, (column) =>
      view?.value.columns.indexOf(column.id),
    );
    return orderedColumns;
  });
  protected itemsRowOptions = computed(() => {
    const rowOptions = this.rowOptions();
    if (!rowOptions) return null;

    return new Map(
      (this.data()?.items ?? []).map((item, index) => [
        this.getItemId(item),
        isFunction(rowOptions) ? rowOptions({ item, index }) : rowOptions,
      ]),
    );
  });
  protected emptyState = computed((): OnyxTableNotFound => {
    if (!this.filtered()) return this.notFound();

    const clearFilters = this.clearFilters();
    return {
      icon: { name: 'search-outlined', size: 32 },
      title: `${this.I18N}.noMatches.title`,
      text: `${this.I18N}.noMatches.text`,
      action: clearFilters
        ? { label: `${this.I18N}.noMatches.clear`, callback: clearFilters }
        : undefined,
    };
  });

  protected emptyStateIcon = computed(
    (): { icon: OnyxIcon; emoji?: string } => {
      const icon = this.emptyState().icon ?? this.DEFAULT_NOT_FOUND_ICON;
      const viewIcon = this.filtered() ? null : this.view()?.value.icon;
      if (!viewIcon) return { icon };

      const { name, emoji } = this.resolveViewIcon(viewIcon);
      return { icon: name ? { ...icon, name } : icon, emoji };
    },
  );

  protected views = signal<OnyxOptionsGroup<string>[]>([]);
  protected viewDropdownAttached = signal(false);

  protected selectedItemsId = computed(() =>
    this.selectedItems().map((item) => this.getItemId(item)),
  );
  protected isPageCheckboxChecked = computed(() => {
    const selectedItemsId = this.selectedItemsId();
    const items = this.data()?.items;
    if (!items) return false;

    return items.every((item) =>
      selectedItemsId.includes(this.getItemId(item)),
    );
  });

  protected isPageCheckboxIndeterminate = computed(() => {
    if (this.isPageCheckboxChecked()) return false;

    const selectedItemsId = this.selectedItemsId();
    const items = this.data()?.items ?? [];

    return items.some((item) => selectedItemsId.includes(this.getItemId(item)));
  });
  protected radioButtonSelection = this.fb.group({
    itemId: this.fb.control<unknown | null>(null),
  });

  protected hasFrozenLeft = computed(
    () => !!(this.selectionType() || this.columns()[0]?.required),
  );
  protected frozenRequiredLeft = computed(() =>
    this.selectionType() != null ? this.SELECTION_COLUMN_WIDTH : 0,
  );
  protected hasFrozenRight = computed(() => this.rowOptions() != null);
  protected hasFrozenShadows = signal({ left: false, right: false });
  protected showRowClick = computed(() => this.rowClick['listeners'] != null);

  private scrollViewportRef = viewChild.required(CdkVirtualScrollViewport);

  protected get stickyTop(): number {
    const offset =
      this.scrollViewportRef().getOffsetToRenderedContentStart() ?? 0;
    return -offset;
  }

  private lastSelectedIndex = signal<number | null>(null);
  private isMultiSelection = signal(false);

  constructor() {
    effect(() => {
      this.selectionType();
      untracked(() => {
        this.selectedItems.set([]);
        this.lastSelectedIndex.set(null);
        this.radioButtonSelection.setValue({ itemId: null });
      });
    });

    const radioButtonSelectedItemId = toSignal(
      this.radioButtonSelection.controls.itemId.valueChanges,
    );
    effect(() => {
      if (this.selectionType() !== OnyxTableSelectionType.RADIO) return;

      const selectedItemId = radioButtonSelectedItemId();
      this.selectedItems.update((selectedItems) =>
        selectedItems.filter((item) => this.getItemId(item) === selectedItemId),
      );
    });

    effect(() => this.selectedItemsIdChange.emit(this.selectedItemsId()));
  }

  public ngOnInit(): void {
    const defaultViews = this.defaultViews()?.map((view) => ({
      ...view,
      default: true,
    }));
    if (defaultViews) {
      this.userSettingsService.reload$
        .pipe(
          startWith(null),
          switchMap(() => {
            const customViews = this.customViews();
            if (!customViews) {
              return of({
                customViews: [],
                selected: null,
              });
            }

            return this.userSettingsService.getSettings(customViews).pipe(
              map(({ settings, selected }) => ({
                customViews: settings.map(
                  ({ uuid, name, value }): OnyxTableView => {
                    const data = JSON.parse(value);
                    if (!isArray(data)) return { uuid, name, value: data };

                    return {
                      uuid,
                      name,
                      value: {
                        columns: data,
                        icon: ONYX_TABLE_VIEW_DEFAULT_ICON,
                      },
                    };
                  },
                ),
                selected,
              })),
            );
          }),
          map(({ customViews, selected }) => {
            const customViewsMap = new Map(
              customViews.map((view) => [view.name, view]),
            );
            const mergedDefaultViews = defaultViews.map((defaultView) => {
              const customView = customViewsMap.get(defaultView.name);
              if (!customView) return defaultView;

              customViewsMap.delete(defaultView.name);

              return {
                ...defaultView,
                uuid: customView.uuid,
                value: {
                  ...defaultView.value,
                  columns: customView.value.columns,
                  filters: customView.value.filters,
                  sort: customView.value.sort ?? defaultView.value.sort,
                },
              };
            });

            return {
              mergedDefaultViews,
              customViews: [...customViewsMap.values()],
              selected,
            };
          }),
          tap(({ mergedDefaultViews, customViews, selected }) => {
            const activeView = [...mergedDefaultViews, ...customViews].find(
              (view) =>
                view.uuid === selected || view.uuid === this.view()?.uuid,
            );
            this.view.set(activeView ?? mergedDefaultViews[0] ?? null);
          }),
          map(({ mergedDefaultViews, customViews }) => {
            const viewToOption = (
              view: OnyxTableView,
            ): OnyxOption<string> & { view: OnyxTableView } => {
              const { name, emoji } = this.resolveViewIcon(view.value.icon);
              return {
                name: view.name,
                ...(emoji && { leftEmoji: emoji }),
                ...(name && { leftIcon: { name, size: 16 } }),
                value: view.uuid,
                view,
              };
            };

            return [
              {
                id: '0.default',
                options: mergedDefaultViews.map(viewToOption),
              },
              ...(customViews.length
                ? [{ id: '1.custom', options: customViews.map(viewToOption) }]
                : []),
            ];
          }),
        )
        .subscribe((views) => this.views.set(views));
    }
  }

  public ngAfterViewInit(): void {
    effect(
      () => {
        this.data();
        setTimeout(() => {
          this.scrollViewportRef().checkViewportSize();
          this.updateFrozenShadows();
        });
      },
      { injector: this.injector },
    );

    effect(
      () => {
        this.pagination();
        setTimeout(() => this.scrollViewportRef().checkViewportSize());
      },
      { injector: this.injector },
    );

    this.scrollViewportRef()
      .elementScrolled()
      .pipe(startWith(null), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.updateFrozenShadows());

    fromEventPattern<void>(
      (handler) => {
        const resizeObserver = new ResizeObserver(() => handler());
        resizeObserver.observe(
          this.scrollViewportRef().elementRef.nativeElement,
        );

        return resizeObserver;
      },
      (_, resizeObserver?: ResizeObserver) => resizeObserver?.disconnect(),
    )
      .pipe(skip(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.updateFrozenShadows());
  }

  protected setView(view: OnyxTableView | null): void {
    if (view == null) return;

    this.view.set(view);

    const customViews = this.customViews();
    if (customViews) {
      const isDefaultView = view.uuid === this.defaultViews()?.[0]?.uuid;
      this.userSettingsService
        .selectSetting(customViews, isDefaultView ? null : view.uuid)
        .subscribe();
    }
  }

  protected addView(): void {
    this.modalService
      .open<OnyxTableViewModalData, OnyxTableViewModalResult>(
        OnyxTableViewModalComponent,
        {
          scope: this.customViews()!,
          columns: this.columns(),
          defaultView: this.defaultViews()![0],
          mode: OnyxFormMode.ADD,
          variant: 'table',
          value: {
            columns: this.visibleColumns().map((column) => column.id),
            filters: this.view()?.value.filters,
            sort: this.view()?.value.sort,
          },
        },
      )
      .result.subscribe((view) => {
        if (isObject(view)) this.setView(view);
      });
  }

  protected editView(view: OnyxTableView): void {
    this.viewDropdownAttached.set(false);
    this.modalService
      .open<OnyxTableViewModalData, OnyxTableViewModalResult>(
        OnyxTableViewModalComponent,
        {
          scope: this.customViews()!,
          columns: this.columns(),
          defaultView: this.defaultViews()![0],
          mode: OnyxFormMode.EDIT,
          variant: 'table',
          ...view,
        },
      )
      .result.subscribe((view) => {
        if (isObject(view)) return this.setView(view);

        if (view === 'delete') {
          this.setView(this.defaultViews()?.[0] ?? null);
        }
      });
  }

  protected togglePageItems(page: OnyxPaginated<T>, selected: boolean): void {
    const pageItemsId = page.items.map((item) => this.getItemId(item));
    const newItemsId = difference(pageItemsId, this.selectedItemsId());

    this.lastSelectedIndex.set(null);
    this.selectedItems.update((selectedItems) => {
      if (selected) {
        return [
          ...selectedItems,
          ...page.items.filter((item, index) => {
            const isDisabled =
              this.selectionDisabled()?.({ item, index }) ?? false;
            const isSelected = !newItemsId.includes(this.getItemId(item));

            return !isDisabled && !isSelected;
          }),
        ];
      }

      return selectedItems.filter(
        (selectedItem) => !pageItemsId.includes(this.getItemId(selectedItem)),
      );
    });
  }

  protected toggleItem(item: T, selected: boolean, index: number): void {
    if (this.selectionType() === OnyxTableSelectionType.RADIO) {
      this.radioButtonSelection.setValue({ itemId: this.getItemId(item) });
      return;
    }

    const lastSelectedIndex = this.lastSelectedIndex();
    const items = this.data()?.items ?? [];

    this.selectedItems.update((selectedItems) => {
      const id = this.getItemId(item);
      if (selected) return [...selectedItems, item];

      return selectedItems.filter(
        (selectedItem) => this.getItemId(selectedItem) !== id,
      );
    });

    if (selected) this.lastSelectedIndex.set(index);

    if (this.isMultiSelection() && lastSelectedIndex !== null) {
      this.selectedItems.update((selectedItems) => {
        const start = Math.min(lastSelectedIndex, index);
        const end = Math.max(lastSelectedIndex, index);
        const selection = items.slice(start, end + 1);

        const isLastSelected = selectedItems.some(
          (item) =>
            this.getItemId(item) === this.getItemId(items[lastSelectedIndex]),
        );
        if (!isLastSelected) return selectedItems;

        const existingIds = selectedItems.map((item) => this.getItemId(item));
        const updatedItems = selection.filter(
          (item) => !existingIds.includes(this.getItemId(item)),
        );
        return [...selectedItems, ...updatedItems];
      });
    }
  }

  protected clearSelection(): void {
    this.selectedItems.set([]);
    this.lastSelectedIndex.set(null);
  }

  protected itemIdentity(_: number, value: T): unknown {
    return this.getItemId(value);
  }

  protected getItemId(item: T): unknown {
    const compareKey = this.compareKey();
    return compareKey ? get(item, compareKey) : item;
  }

  @HostListener('window:keydown.Shift')
  protected startMultiSelecting(): void {
    this.isMultiSelection.set(true);
  }

  @HostListener('window:keyup.Shift')
  protected stopMultiSelecting(): void {
    this.isMultiSelection.set(false);
  }

  private updateFrozenShadows(): void {
    const scrollViewport = this.scrollViewportRef();
    this.hasFrozenShadows.set({
      left: scrollViewport.measureScrollOffset('left') > 0,
      right: scrollViewport.measureScrollOffset('right') > 0,
    });
  }

  private resolveViewIcon(icon: OnyxTableViewIcon): {
    name?: OnyxIconName;
    emoji?: string;
  } {
    return {
      name: UtilityHelper.isOnyxIcon(icon) ? icon : undefined,
      emoji: UtilityHelper.isEmoji(icon) ? icon : undefined,
    };
  }

  private mapActions(
    actions: OnyxTableColumn<T>['actions'],
  ): OnyxTableCustomAction<T>[] {
    if (!actions) return [];

    return orderBy(actions)
      .slice(0, 2)
      .map((action) => {
        const ICONS: Record<OnyxTableAction, OnyxIcon> = {
          [OnyxTableAction.EDIT]: { name: 'edit-filled', size: 20 },
          [OnyxTableAction.OPEN_PROFILE]: {
            name: 'arrow-right-outlined',
            size: 20,
          },
          [OnyxTableAction.CALL]: { name: 'telephone-filled', size: 20 },
          [OnyxTableAction.COPY]: { name: 'copy-filled', size: 20 },
        };
        const TOOLTIPS: Record<OnyxTableAction, string> = {
          [OnyxTableAction.EDIT]: `${this.I18N}.actions.edit`,
          [OnyxTableAction.OPEN_PROFILE]: `${this.I18N}.actions.openProfile`,
          [OnyxTableAction.CALL]: `${this.I18N}.actions.call`,
          [OnyxTableAction.COPY]: `${this.I18N}.actions.copy`,
        };

        const isDefault = Object.values(OnyxTableAction).includes(
          action.icon as OnyxTableAction,
        );
        return {
          icon: isDefault ? ICONS[action.icon as OnyxTableAction] : action.icon,
          callback: action.callback,
          tooltip:
            action.tooltip ??
            (isDefault ? TOOLTIPS[action.icon as OnyxTableAction] : undefined),
          hidden: action.hidden,
        };
      });
  }
}
